import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Shield, Users, MapPin, Clock, CheckCircle, ArrowRight, ArrowLeft, Target, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_tabbibak-algeria/artifacts/bch5rgxl_radja.jpg";

const About = () => {
  const { t, isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-[#f0f9ff] to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-[#E6F4FB] text-[#0596DE] text-sm font-medium rounded-full mb-6">
                {t('about_badge')}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
                {t('about_title_1')}<span className="text-[#0596DE]">{t('about_title_highlight')}</span>
              </h1>
              <p className="text-lg text-[#64748B] leading-relaxed mb-8">
                {t('about_subtitle')}
              </p>
              <div className="flex items-center gap-4">
                <Link to="/search">
                  <Button className="rounded-full bg-[#0596DE] hover:bg-[#047CBA] h-12 px-8" data-testid="about-search-btn">
                    {t('about_search_btn')} <Arrow className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/doctor/register">
                  <Button variant="outline" className="rounded-full h-12 px-8">
                    {t('about_doctor_btn')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <img src={HERO_IMAGE} alt="Tabbibak" className="w-full max-w-md rounded-3xl shadow-xl object-cover h-[400px]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#F8FAFC] rounded-3xl p-8 md:p-10 border border-[#E2E8F0]" data-testid="mission-card">
              <div className="w-14 h-14 rounded-2xl bg-[#E6F4FB] flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#0596DE]" />
              </div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">{t('about_mission_title')}</h2>
              <p className="text-[#64748B] leading-relaxed">{t('about_mission_desc')}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-3xl p-8 md:p-10 border border-[#E2E8F0]" data-testid="vision-card">
              <div className="w-14 h-14 rounded-2xl bg-[#D1FAE5] flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-[#10B981]" />
              </div>
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">{t('about_vision_title')}</h2>
              <p className="text-[#64748B] leading-relaxed">{t('about_vision_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">{t('about_values_title')}</h2>
            <p className="text-[#64748B] mt-3">{t('about_values_subtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, color: '#EF4444', bg: '#FEE2E2', title: t('about_val1_title'), desc: t('about_val1_desc') },
              { icon: Shield, color: '#0596DE', bg: '#E6F4FB', title: t('about_val2_title'), desc: t('about_val2_desc') },
              { icon: MapPin, color: '#10B981', bg: '#D1FAE5', title: t('about_val3_title'), desc: t('about_val3_desc') },
              { icon: Clock, color: '#F59E0B', bg: '#FEF3C7', title: t('about_val4_title'), desc: t('about_val4_desc') },
            ].map(({ icon: Icon, color, bg, title, desc }, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#E2E8F0] text-center hover:shadow-md transition-shadow">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: bg }}>
                  <Icon className="w-7 h-7" style={{ color }} />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">{title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0596DE] rounded-3xl p-10 md:p-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
              {[
                { num: '18K+', label: "Médecins référencés" },
                { num: '69', label: t('about_stats_wilayas') },
                { num: '22', label: t('about_stats_specialties') },
                { num: '100%', label: t('about_stats_free') },
                { num: '24/7', label: t('about_stats_online') },
              ].map(({ num, label }, i) => (
                <div key={i}>
                  <div className="text-3xl md:text-4xl font-bold font-['Outfit']">{num}</div>
                  <div className="text-white/70 text-sm mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">{t('about_faq_title')}</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: t('about_faq1_q'), a: t('about_faq1_a') },
              { q: t('about_faq2_q'), a: t('about_faq2_a') },
              { q: t('about_faq3_q'), a: t('about_faq3_a') },
              { q: t('about_faq4_q'), a: t('about_faq4_a') },
            ].map(({ q, a }, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
                <h3 className="font-semibold text-[#0F172A] mb-2">{q}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
            {t('about_cta_title')}
          </h2>
          <p className="text-[#64748B] mb-8 max-w-lg mx-auto">
            {t('about_cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button className="rounded-full bg-[#0596DE] hover:bg-[#047CBA] h-12 px-8 text-base">
                {t('about_cta_search')}
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="rounded-full h-12 px-8 text-base">
                {t('about_cta_register')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10">
            <div>
              <span className="font-bold text-2xl font-['Outfit'] text-[#0596DE]">Tabbibak.com</span>
              <p className="text-gray-400 text-sm mt-3 leading-relaxed">{t('footer_desc')}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm tracking-wider uppercase text-gray-300">{t('footer_patients')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><Link to="/search" className="hover:text-white transition-colors">{t('footer_find')}</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">{t('footer_create_account')}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t('footer_login')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm tracking-wider uppercase text-gray-300">{t('footer_doctors')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><Link to="/doctor/register" className="hover:text-white transition-colors">{t('footer_register')}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t('footer_access')}</Link></li>
                <li><Link to="/partenariat" className="hover:text-white transition-colors">Partenariats</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm tracking-wider uppercase text-gray-300">{t('footer_contact')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li>contact@tabbibak.com</li>
                <li>Tizi Ouzou & Oran, Algérie</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} Tabbibak.com - {t('footer_rights')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
